/**
 * Geocoding service for converting addresses to lat/lng coordinates
 * Uses Google Maps Geocoding API
 */

// Resolve the correct API key based on selected environment
function getMapsApiKey(): string {
  const direct = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (direct) return direct;

  // Try per-environment keys if provided
  const envFromStorage = typeof window !== 'undefined' ? (localStorage.getItem('selectedEnvironment') || '').toLowerCase() : '';
  const reactAppEnv = (process.env.REACT_APP_ENV || '').toLowerCase();
  const nodeEnv = (process.env.NODE_ENV || '').toLowerCase();
  
  // Use localStorage preference, or build-time env, or NODE_ENV as fallback
  let buildEnv = envFromStorage || reactAppEnv;
  
  // If no explicit REACT_APP_ENV, use NODE_ENV (Vercel sets this automatically)
  if (!buildEnv && nodeEnv === 'production') {
    buildEnv = 'production';
  } else if (!buildEnv) {
    buildEnv = 'development';
  }
  
  const envName = buildEnv.toLowerCase();

  const byEnv: Record<string, string | undefined> = {
    development: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_DEV,
    preview: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_PREVIEW,
    production: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_PROD,
  };
  
  const selectedKey = byEnv[envName];
  console.log('[Geocoding] Resolving Maps API Key:', { envFromStorage, reactAppEnv, nodeEnv, buildEnv, envName, hasKey: !!selectedKey });
  
  return selectedKey || '';
}

const GOOGLE_MAPS_API_KEY = getMapsApiKey();
const GOOGLE_MAPS_REGION = process.env.REACT_APP_GOOGLE_MAPS_REGION || 'us';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Geocode an address string using Google Maps Geocoding API
 * @param address The address string to geocode
 * @returns { latitude, longitude } or throws on error
 */
export async function getCoordinatesForAddress(address: string): Promise<Coordinates> {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('Google Maps API key is not configured');
  }

  // Add region bias to improve matching, especially for partial inputs
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&region=${encodeURIComponent(GOOGLE_MAPS_REGION)}&key=${GOOGLE_MAPS_API_KEY}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch geocoding results');
    }
    
    const data = await response.json();
    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      const message =
        data.error_message
          ? `${data.status}: ${data.error_message}`
          : data.status === 'ZERO_RESULTS'
            ? 'No results. Try a more complete address (e.g., "123 Main St, City, State").'
            : `Geocoding failed with status: ${data.status}`;
      throw new Error(message);
    }
    
    const location = data.results[0].geometry.location;
    return {
      latitude: location.lat,
      longitude: location.lng,
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error instanceof Error ? error : new Error('Failed to geocode address');
  }
}

